import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Carousel } from 'antd';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useMediaQuery } from '@mui/material';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import { useRouter } from 'next/router';

// Styled components
const SectionTitle = styled(Typography)({
  fontSize: '33px',
  fontWeight: 'bold',
  marginBottom: '24px',
});

const ViewAllButton = styled(Button)({
  fontSize: '16px',
  color: '#333',
  textTransform: 'none',
  float: 'right',
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

const ItemCard = styled(Box)({
  margin: '0 8px',
  borderRadius: '10px',
  overflow: 'hidden',
  position: 'relative',
});

// Responsive styled components
const createImageContainer = (isTablet: boolean, isMobile: boolean) => styled(Box)({
  overflow: 'hidden',
  borderRadius: '10px',
  position: 'relative',
  width: '100%',
  aspectRatio: isMobile ? '4/3' : 'auto', // 4:3 aspect ratio สำหรับ mobile
  height: isMobile ? 'auto' : (isTablet ? '200px' : '274px'), // auto height สำหรับ mobile เพื่อใช้ aspect ratio
});

const createItemContent = (isTablet: boolean, isMobile: boolean) => styled(Box)({
  padding: isMobile ? '8px' : (isTablet ? '12px' : '16px'), // ลด padding ใน mobile และ tablet
  textAlign: 'center',
  backgroundColor: 'transparent',
});

const createItemTitle = (isTablet: boolean, isMobile: boolean) => styled(Typography)({
  fontSize: isMobile ? '19px !important' : (isTablet ? '18px !important' : '23px !important'), // ใช้ขนาด 19px จาก Figma สำหรับ mobile
  fontWeight: '500',
  marginBottom: isMobile ? '4px' : (isTablet ? '6px' : '8px'),
});

const createItemCount = (isTablet: boolean, isMobile: boolean) => styled(Typography)({
  fontSize: isMobile ? '16px !important' : (isTablet ? '16px !important' : '19px !important'), // ใช้ขนาด 16px จาก Figma สำหรับ mobile
  fontWeight: '400', // ใช้ fontWeight 400 ตาม Figma สำหรับ "รายการ"
  '& .count': {
    color: '#F4762A',
    fontWeight: '500', // Count number ใช้ fontWeight 500
    fontSize: isMobile ? '19px !important' : (isTablet ? '16px !important' : '19px !important'), // Count number ใช้ขนาด 19px จาก Figma สำหรับ mobile
  },
});

const ItemImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
});

const StyledCarousel = styled(Carousel)<{ isMobile?: boolean }>(({ isMobile }) => ({
  '.slick-dots': {
    bottom: '-5px',
  },
  '.slick-dots li button': {
    backgroundColor: 'rgba(211, 211, 211, 1)',
    borderRadius: '50%',
    width: '10px',
    height: '10px',
    '&:before': {
      content: '""',
    },
  },
  '.slick-dots li.slick-active button': {
    backgroundColor: 'rgba(244, 118, 42, 1)',
    width: '10px',
    height: '10px',
  },
  '.slick-prev, .slick-next': {
    display: 'none !important',
  },
  // Mobile rows-based layout styles (similar to CategoriesCarousel)
  ...(isMobile && {
    '.slick-slide': {
      '& > div': {
        display: 'flex',
        justifyContent: 'center',
        padding: '4px 0', // เพิ่ม spacing ระหว่างแถว
      },
    },
    '.slick-dots': {
      bottom: '-25px',
      '& li': {
        margin: '0 3px',
      },
    },
  }),
}));

interface AnnouncementCarouselProps {
  title?: string;
  viewAllLink?: string;
  items?: any[];
  sx?: any;
  isTablet?: boolean;
  isMobile?: boolean;
}

const AnnouncementCarousel: React.FC<AnnouncementCarouselProps> = ({ 
  title = "ประกาศขาย", 
  viewAllLink = "#",
  items = [],
  sx = {},
  isTablet = false,
  isMobile = false,
}) => {
  const router = useRouter();

  // Create dynamic styled components based on device mode
  const ImageContainer = createImageContainer(isTablet, isMobile);
  const ItemContent = createItemContent(isTablet, isMobile);
  const ItemTitle = createItemTitle(isTablet, isMobile);
  const ItemCount = createItemCount(isTablet, isMobile);

  // Default items if none provided
  const defaultItems = [
    {
      id: 1,
      image: '/path/to/condo.jpg',
      title: 'ขายคอนโด',
      count: 1200,
      link: '#'
    },
    {
      id: 2,
      image: '/path/to/house.jpg',
      title: 'ขายบ้านเดี่ยว',
      count: 1200,
      link: '#'
    },
    {
      id: 3,
      image: '/path/to/townhouse.jpg',
      title: 'ขายทาวน์เฮ้าส์-โฮม',
      count: 1200,
      link: '#'
    },
    {
      id: 4,
      image: '/path/to/land.jpg',
      title: 'ขายที่ดิน',
      count: 850,
      link: '#'
    },
    {
      id: 5,
      image: '/path/to/commercial.jpg',
      title: 'ขายอาคารพาณิชย์',
      count: 650,
      link: '#'
    },
  ];

  const displayItems = items.length > 0 ? items : defaultItems;

  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: isMobile ? 1 : (isTablet ? 3 : 3), // แสดง 1 column ใน mobile, 3 ใน tablet/desktop
    slidesToScroll: isMobile ? 1 : (isTablet ? 3 : 3), // scroll ทีละ 1 ใน mobile (จะเป็น 3 items เพราะมี 3 rows)
    rows: isMobile ? 3 : 1, // 3 แถวใน mobile (แนวตั้ง), 1 แถวใน tablet/desktop
    slidesPerRow: 1,
    initialSlide: 0,
    draggable: true,
    swipe: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
          rows: 1,
          slidesPerRow: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1, // 1 column
          slidesToScroll: 1, // scroll ทีละ 1 column (3 items)
          rows: 3, // 3 แถว = 3 รายการแนวตั้ง
          slidesPerRow: 1,
        }
      }
    ]
  };

  const handleViewAll = () => {
    router.push(viewAllLink);
  };

  const handleItemClick = (link) => {
    router.push(link);
  };

  return (
    <Box sx={{ 
      maxWidth: '100%', 
      padding: '0',
      position: 'relative',
      ...sx
    }}>
      <Box sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        padding: isMobile ? '12px' : (isTablet ? '18px' : '24px'), // ลด padding ใน mobile และ tablet
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.05)',
        border: '1px solid #F5F5F5',
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: isMobile ? 'center' : 'space-between', 
          alignItems: 'center',
          marginBottom: isMobile ? '12px' : (isTablet ? '18px' : '24px') // ลด margin ใน mobile และ tablet
        }}>
          <Typography 
            component="h2"
            sx={{
              fontSize: isMobile ? '28px !important' : (isTablet ? '28px' : '33px') + " !important", // ใช้ขนาดจาก Figma สำหรับ mobile
              fontWeight: '500', // ใช้ fontWeight 500 ตาม Figma
              margin: 0, // Reset default h2 margin
            }}
          >
            {title}
          </Typography>
          {!isMobile && (
            <ViewAllButton 
              endIcon={<ArrowCircleRightOutlinedIcon sx={{ color: '#F4762A' }} />} 
              onClick={handleViewAll}
              sx={{
                fontSize: isTablet ? '14px' : '16px', // ลดขนาดปุ่มใน tablet
              }}
            >
              ดูทั้งหมด
            </ViewAllButton>
          )}
        </Box>

        <StyledCarousel {...settings} isMobile={isMobile}>
          {displayItems.map((item) => (
            <div key={item.id}>
              <ItemCard onClick={() => handleItemClick(item.link)} sx={{ 
                cursor: 'pointer',
                margin: isMobile ? '0 2px' : (isTablet ? '0 4px' : '0 8px'), // ลด margin ใน mobile และ tablet
              }}>
                <ImageContainer>
                  <ItemImage src={item.image} alt={item.title} />
                </ImageContainer>
                <ItemContent>
                  <ItemTitle>{item.title}</ItemTitle>
                  <ItemCount>
                    <span className="count">{item.count.toLocaleString()}</span> รายการ
                  </ItemCount>
                </ItemContent>
              </ItemCard>
            </div>
          ))}
        </StyledCarousel>

        {/* Mobile View All Button - positioned below dots */}
        {isMobile && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginTop: '30px' // Space below dots
          }}>
            <ViewAllButton 
              endIcon={<ArrowCircleRightOutlinedIcon sx={{ color: '#F4762A' }} />} 
              onClick={handleViewAll}
              sx={{
                fontSize: '16px !important', // ใช้ขนาด 16px จาก Figma
                fontWeight: '400', // ใช้ fontWeight 400 ตาม Figma
                textTransform: 'none',
                color: '#333',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              ดูทั้งหมด
            </ViewAllButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AnnouncementCarousel;